import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PortletService from '../../_services/PortletService';
import { IconKey } from '../../constants/iconMap';
import IconDisplay from '../../components/IconDisplay';
import './portlet-group.css';

interface PortletIcon {
  type: 'emoji' | 'image' | 'lucide' | 'url' | 'key';
  value?: string;
  key?: IconKey;
}

interface PortletChild {
  id: string;
  url: string;
  title: string;
  description?: string;
  icon?: PortletIcon;
  gradient?: string;
  openInNewTab?: boolean;
}

interface GroupPortlet {
  id: string;
  type: 'group';
  title: string;
  icon?: PortletIcon;
  children: PortletChild[];
}

// A "?" / blank emoji is a placeholder that was never given a real icon.
// Treat it as absent so a sensible fallback renders instead of a broken glyph.
const normalizeIcon = (
  icon: PortletIcon | undefined,
  fallback: PortletIcon
): PortletIcon => {
  if (!icon) return fallback;
  if (icon.type === 'emoji') {
    const v = (icon.value ?? '').trim();
    if (!v || v === '?') return fallback;
  }
  return icon;
};

const PortletGroup: React.FC = () => {
  const { portletId } = useParams<{ portletId: string }>();
  const history = useHistory();
  const [groupPortlet, setGroupPortlet] = useState<GroupPortlet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPortlet();
  }, [portletId]);

  const loadPortlet = async () => {
    try {
      setLoading(true);
      setError(null);

      const portlets = await PortletService.getPortlets();

      // Find the group portlet by ID - need to cast since getPortlets returns basic Portlet type
      const found = (portlets as any[]).find(
        (p) => p.id === portletId && p.type === 'group'
      ) as GroupPortlet | undefined;

      if (!found) {
        setError('Group portlet not found');
        return;
      }

      setGroupPortlet(found);
    } catch (err) {
      console.error('Error loading portlet:', err);
      setError('Failed to load portlet');
    } finally {
      setLoading(false);
    }
  };

  const handleChildClick = (child: PortletChild) => {
    if (child.openInNewTab) {
      window.open(child.url, '_blank');
    } else if (child.url.startsWith('http')) {
      window.location.href = child.url;
    } else {
      history.push(child.url);
    }
  };

  const handleBack = () => {
    history.push('/');
  };

  if (loading) {
    return (
      <div className="portlet-group">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !groupPortlet) {
    return (
      <div className="portlet-group">
        <div className="error-state">
          <p>{error || 'Portlet not found'}</p>
          <button className="btn-back" onClick={handleBack}>
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="portlet-group">
      <header className="group-header">
        <button className="btn-back" onClick={handleBack}>
          ← Back
        </button>
        <div className="group-title">
          <span
            className="group-icon"
            style={{ background: 'var(--gradient-brand)' }}
          >
            <IconDisplay
              icon={normalizeIcon(groupPortlet.icon, { type: 'lucide', value: 'layout-grid' })}
              size={22}
            />
          </span>
          <h1>{groupPortlet.title}</h1>
        </div>
      </header>

      <div className="children-grid">
        {groupPortlet.children.map((child) => {
          const gradient = child.gradient || 'var(--gradient-brand)';

          return (
            <div
              key={child.id}
              className="child-card"
              onClick={() => handleChildClick(child)}
            >
              <div className="child-icon" style={{ background: gradient }}>
                <IconDisplay
                  icon={normalizeIcon(child.icon, { type: 'lucide', value: 'file-text' })}
                  size={24}
                />
              </div>
              <div className="child-info">
                <h3>{child.title}</h3>
                {child.description && (
                  <p className="child-description">{child.description}</p>
                )}
              </div>
              <span className="child-arrow" aria-hidden="true">
                <IconDisplay
                  icon={{ type: 'lucide', value: child.openInNewTab ? 'arrow-up-right' : 'chevron-right' }}
                  size={18}
                />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PortletGroup;
